/**
 * AccommodationScanStation - Dedicated housing/accommodation access scanner
 * Features:
 * - QR/badge scanning for accommodation verification
 * - Visual green/red access indicators
 * - Building/room assignment display
 * - Access history and statistics
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { getToken } from '@/lib/authToken';
import { 
  Camera, CameraOff, Volume2, VolumeX, X, 
  CheckCircle, XCircle, RefreshCw,
  Building, Home, BedDouble, DoorOpen, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccommodationScanStation = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [recentAccess, setRecentAccess] = useState([]);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [accessPoint, setAccessPoint] = useState('main');
  const [cameraList, setCameraList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  
  const html5QrCodeRef = useRef(null);
  const scannerContainerId = 'accommodation-scanner-container';

  useEffect(() => {
    Html5Qrcode.getCameras().then(cameras => {
      setCameraList(cameras);
      const backCamera = cameras.find(c => 
        c.label.toLowerCase().includes('back') || 
        c.label.toLowerCase().includes('environment')
      );
      setSelectedCamera(backCamera?.id || cameras[0]?.id);
    }).catch(console.error);

    fetchEvent();
    fetchRecentAccess();
    
    const interval = setInterval(fetchRecentAccess, 15000);
    
    return () => {
      clearInterval(interval);
      stopScanner();
    };
  }, [eventId]);

  // iter-438c: removed local shadow `const getToken = () => getToken();`
  // which infinite-recursed and threw RangeError before any scan fetch.
  // Uses the imported getToken from '@/lib/authToken' (line 17).

  const fetchEvent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`);
      if (res.ok) setEvent(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchRecentAccess = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/accommodation-scan/history?limit=15`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setRecentAccess(await res.json());
    } catch (e) { console.error(e); }
  };

  const playSound = (type) => {
    if (!soundEnabled) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    if (type === 'success') {
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    } else {
      osc.frequency.value = 330;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    }
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  };

  const startScanner = async () => {
    if (!selectedCamera) {
      setError('No camera available');
      return;
    }
    
    setError(null);
    setResult(null);
    setScanning(true);

    try {
      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
      await html5QrCodeRef.current.start(
        selectedCamera,
        { fps: 15, qrbox: { width: 250, height: 250 } },
        handleScanResult,
        () => {}
      );
    } catch (err) {
      console.error('Scanner start failed:', err);
      setError('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (e) { console.error(e); }
    }
    setScanning(false);
  };

  const handleScanResult = useCallback(async (decodedText) => {
    // Pause scanner
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.pause(); } catch (e) {}
    }

    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/accommodation-scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          qr_data: decodedText,
          building: selectedBuilding || null,
          access_point: accessPoint
        })
      });

      const data = await res.json();
      
      if (data.success && data.allowed) {
        playSound('success');
        setResult({
          success: true,
          allowed: true,
          ...data
        });
        fetchRecentAccess();
      } else {
        playSound('error');
        setResult({
          success: false,
          allowed: false,
          ...data
        });
      }
    } catch (err) {
      playSound('error');
      setResult({
        success: false,
        allowed: false,
        message: 'Network error. Please try again.',
        color: 'red'
      });
    }

    // Auto-resume after 4 seconds
    setTimeout(() => {
      setResult(null);
      if (html5QrCodeRef.current && scanning) {
        try { html5QrCodeRef.current.resume(); } catch (e) {}
      }
    }, 4000);
  }, [eventId, selectedBuilding, accessPoint, scanning]);

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="accommodation-scan-station">
      {/* Header */}
      <div className="bg-indigo-600 p-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Building className="h-6 w-6" />
            {event?.name || 'Accommodation Access'}
          </h1>
          <div className="flex items-center gap-2 text-indigo-100 text-sm">
            <span>Housing Access Control</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-white border-indigo-300 hover:bg-indigo-700"
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white border-indigo-300 hover:bg-indigo-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-80px)]">
        {/* Scanner Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          {/* Building/Access Point Selection */}
          <div className="mb-6 flex gap-4 flex-wrap justify-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Building:</label>
              <Input
                placeholder="Enter building name"
                value={selectedBuilding}
                onChange={(e) => setSelectedBuilding(e.target.value)}
                className="w-44 bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Access Point:</label>
              <Select value={accessPoint} onValueChange={setAccessPoint}>
                <SelectTrigger className="w-36 bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main Entrance</SelectItem>
                  <SelectItem value="side">Side Entrance</SelectItem>
                  <SelectItem value="emergency">Emergency Exit</SelectItem>
                  <SelectItem value="parking">Parking Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {!scanning ? (
            <div className="text-center">
              <div className="relative inline-block mb-8">
                <div className="w-32 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Home className="h-16 w-16 text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4">Ready to Scan</h2>
              <p className="text-gray-400 mb-8 max-w-sm">
                Scan delegate badge to verify accommodation access
              </p>
              <Button 
                onClick={startScanner} 
                size="lg"
                className="text-xl px-8 py-6 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="start-accommodation-scanner-btn"
              >
                <Camera className="h-6 w-6 mr-2" />
                Start Scanner
              </Button>
            </div>
          ) : (
            <div className="relative w-full max-w-lg">
              <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
                <div id={scannerContainerId} className="w-full aspect-square" />
                
                {/* Result Overlay */}
                {result && (
                  <div 
                    className={`absolute inset-0 flex items-center justify-center ${
                      result.allowed 
                        ? 'bg-green-500/95' 
                        : 'bg-red-500/95'
                    }`}
                    data-testid="accommodation-scan-result"
                  >
                    <div className="text-center p-6 max-w-md w-full">
                      <div className="mb-3">
                        {result.allowed ? (
                          <CheckCircle className="h-20 w-20 mx-auto text-white" />
                        ) : (
                          <XCircle className="h-20 w-20 mx-auto text-white" />
                        )}
                      </div>
                      <div className="text-3xl font-bold mb-2 text-white">
                        {result.allowed ? 'ACCESS GRANTED' : 'ACCESS DENIED'}
                      </div>
                      <div className="text-xl text-white/90 mb-4">
                        {result.message}
                      </div>
                      {result.delegate && (
                        <div className="text-white/90 space-y-2">
                          <div className="text-2xl font-semibold">{result.delegate.name}</div>
                          {result.delegate.tier && (
                            <Badge className="bg-white/20 text-white text-base px-3 py-1">
                              {result.delegate.tier}
                            </Badge>
                          )}
                          {result.delegate.accommodation && (
                            <div className="mt-4 p-3 bg-white/10 rounded-lg">
                              <div className="flex items-center justify-center gap-2 mb-2">
                                <BedDouble className="h-5 w-5" />
                                <span className="font-medium">Accommodation Details</span>
                              </div>
                              <div className="text-lg">
                                {result.delegate.accommodation.building && (
                                  <div>🏢 {result.delegate.accommodation.building}</div>
                                )}
                                {result.delegate.accommodation.room_number && (
                                  <div>🚪 Room {result.delegate.accommodation.room_number}</div>
                                )}
                                {result.delegate.accommodation.type && (
                                  <Badge className="mt-2 bg-white/20">
                                    {result.delegate.accommodation.type}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex justify-center gap-3">
                <Button 
                  variant="outline" 
                  onClick={stopScanner} 
                  className="text-white border-gray-600 hover:bg-gray-700"
                >
                  <CameraOff className="h-4 w-4 mr-2" />
                  Stop Scanner
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
              {error}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 bg-gray-800 p-4 overflow-y-auto">
          {/* Current Location */}
          <Card className="bg-indigo-600/20 border-indigo-500/30 mb-4">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <DoorOpen className="h-4 w-4 text-indigo-300" />
                <span className="text-indigo-300 font-medium">Current Location</span>
              </div>
              <div className="text-white">
                {selectedBuilding || 'All Buildings'}
              </div>
              <div className="text-sm text-indigo-300 capitalize mt-1">
                {accessPoint} Entrance
              </div>
            </CardContent>
          </Card>

          {/* Recent Access */}
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Access
          </h3>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {recentAccess.map((access, i) => (
              <div 
                key={access.id || i}
                className={`p-2 rounded-lg text-sm ${
                  access.status === 'granted' 
                    ? 'bg-green-900/30 border border-green-700/30' 
                    : 'bg-red-900/30 border border-red-700/30'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-white">{access.delegate_name}</span>
                  {access.status === 'granted' ? (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                </div>
                <div className="flex justify-between text-gray-400 text-xs mt-1">
                  <span>{access.building || 'N/A'}</span>
                  <span>{new Date(access.scanned_at).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
            {recentAccess.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">No access logs yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccommodationScanStation;
